// ui/src/utils/apiError.ts

export interface ApiValidationDetail {
  field?: string;
  message?: string;
  code?: string;
}

const REEMBOLSO_FIELD_LABELS: Record<string, string> = {
  veiculo_placa: 'Placa do veículo',
  veiculo_modelo: 'Modelo do veículo',
  adiantamento: 'Adiantamento',
  despesas: 'Despesas',
  'pagamento.tipo': 'Forma de pagamento',
  'pagamento.pix_chave': 'Chave PIX',
  'pagamento.pix_tipo_chave': 'Tipo de chave PIX',
  'pagamento.banco': 'Banco',
  'pagamento.agencia': 'Agência',
  'pagamento.conta': 'Conta',
  'pagamento.digito': 'Dígito da conta',
};

function fieldLabel(field: string | undefined): string {
  if (!field) return 'Campo';
  return REEMBOLSO_FIELD_LABELS[field] ?? field.replace(/_/g, ' ').replace(/\./g, ' › ');
}

function humanizeDetail(detail: ApiValidationDetail): string {
  const label = fieldLabel(detail.field);
  const msg = (detail.message || '').trim();

  if (msg && !/^too big|^too small|^invalid|^expected/i.test(msg)) {
    return msg;
  }

  if (detail.code === 'too_big') {
    const max = msg.match(/<=?\s*(\d+)/)?.[1];
    return max ? `${label}: use no máximo ${max} caracteres.` : `${label}: valor muito longo.`;
  }

  if (detail.code === 'too_small') {
    const min = msg.match(/>=?\s*(\d+)/)?.[1];
    return min ? `${label}: use no mínimo ${min} caracteres.` : `${label}: valor muito curto.`;
  }

  if (detail.code === 'invalid_type') {
    return `${label}: valor inválido.`;
  }

  return msg ? `${label}: ${msg}` : `${label}: valor inválido.`;
}

/** Extrai mensagem legível de erros da API (inclui array `details` de validação). */
export function extractApiErrorMessage(error: unknown, fallback = 'Ocorreu um erro. Tente novamente.'): string {
  const data = (error as { response?: { data?: Record<string, unknown> } })?.response?.data;
  if (!data) return fallback;

  const details = data.details;
  if (Array.isArray(details) && details.length > 0) {
    return details
      .map((d) => humanizeDetail(d as ApiValidationDetail))
      .join(' ');
  }

  const msg = data.error ?? data.message;
  return typeof msg === 'string' && msg.trim() ? msg : fallback;
}

export function extractApiErrorDetails(error: unknown): string[] {
  const data = (error as { response?: { data?: Record<string, unknown> } })?.response?.data;
  const details = data?.details;
  if (!Array.isArray(details) || !details.length) {
    const msg = extractApiErrorMessage(error, '');
    return msg ? [msg] : [];
  }
  return details.map((d) => humanizeDetail(d as ApiValidationDetail));
}
